import React, { useState, useEffect } from 'react';
import { useDiagnostics } from '../diagnostics/DiagnosticsContext';

export default function DataTable({
  rawData = [],
  selectedFields = [],
  SENSOR_NAME_MAP = {},
  SENSOR_FIELDS = {}
}) {
  const [tableData, setTableData] = useState([]);
  const { badDataBin, addToBadDataBin } = useDiagnostics();

  useEffect(() => {
    if (!selectedFields.length) {
      setTableData([]);
      return;
    }

    const grouped = {};
    rawData.forEach(d => {
      const fieldKey = `${d._measurement}:${d._field}`;
      if (!selectedFields.includes(fieldKey)) return;
      
      const value = d._value;
      const timeKey = new Date(d._time).toISOString().slice(0, 16);

      if (!grouped[timeKey]) {
        grouped[timeKey] = { 
          time: timeKey,
          buoy_id: d.buoy_id,
          timestamp: new Date(d._time).getTime()
        };
      }

      grouped[timeKey][fieldKey] = value;
      if (d.mode && (fieldKey === 'procv_co2:pCO2' || fieldKey === 'mini_co2:pCO2')) {
        grouped[timeKey][`${fieldKey}_mode`] = d.mode;
      }
    });

    const sortedData = Object.values(grouped).sort((a, b) => b.timestamp - a.timestamp);
    setTableData(sortedData);
  }, [rawData, selectedFields]);

  function getLabel(fieldKey) {
    const [sensor, field] = fieldKey.split(":");
    const prettySensor = SENSOR_NAME_MAP?.[sensor] || sensor;
    const prettyField = SENSOR_FIELDS?.[sensor]?.[field] || field;
    return `${prettySensor} ${prettyField}`;
  }

  // Detect bad data points based on realistic ranges
  function isBadDataPoint(fieldKey, value) {
    if (typeof value !== 'number' || isNaN(value) || !isFinite(value)) return true;
    
    const [sensor, field] = fieldKey.split(':');
    
    // Define realistic ranges for different sensors
    const ranges = {
      'procv_co2:pCO2': { min: 300, max: 600 },
      'mini_co2:pCO2': { min: 300, max: 600 },
      'CTD:temperature_c': { min: -5, max: 50 },
      'CTD:conductivity_s_per_m': { min: 0, max: 7 },
      'CTD:pressure_dbar': { min: 0, max: 100 },
      'CTD:oxygen_ml_per_l': { min: 0, max: 15 },
      'CTD:salinity_psu': { min: 0, max: 40 }
    };
    
    const range = ranges[fieldKey];
    if (range) {
      return value < range.min || value > range.max;
    }
    
    // General outlier detection: more than 5 standard deviations from mean
    return Math.abs(value) > 1000000; // Very large outlier threshold
  }

  // Handle clicking on a bad data point to exclude it
  function handleBadDataClick(row, fieldKey) {
    const pointKey = `${row.buoy_id}-${row.time}-${fieldKey}`;
    const point = {
      pointKey,
      buoyId: row.buoy_id,
      time: row.time,
      field: fieldKey,
      value: row[fieldKey],
      timestamp: row.timestamp
    };
    
    addToBadDataBin(point);
  }

  // Check if a data point is already excluded
  function isExcluded(row, fieldKey) {
    const pointKey = `${row.buoy_id}-${row.time}-${fieldKey}`;
    return badDataBin.some(bad => bad.pointKey === pointKey);
  }

  // Scroll to first bad data point
  function scrollToFirstBadData() {
    const firstBadCell = document.querySelector('td[data-bad-data="true"]');
    if (firstBadCell) {
      firstBadCell.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'center'
      });
      // Flash the cell to highlight it
      firstBadCell.style.animation = 'flash 2s ease-in-out';
      setTimeout(() => {
        if (firstBadCell.style.animation) {
          firstBadCell.style.animation = '';
        }
      }, 2000);
    }
  }

  if (!selectedFields.length) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '3rem', 
        color: '#6c757d',
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ margin: '0 0 1rem 0', color: '#2c3e50' }}>No Data Selected</h3>
        <p style={{ margin: 0 }}>Please select data fields from the sidebar to view the table.</p>
      </div>
    );
  }

  return (
    <div style={{ 
      background: 'white', 
      borderRadius: '8px', 
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      overflow: 'hidden'
    }}>
      {/* CSS for flash animation */}
      <style>{`
        @keyframes flash {
          0%, 100% { background-color: #f8d7da; }
          50% { background-color: #dc3545; color: white; }
        }
      `}</style>
      <div style={{ 
        background: '#f8f9fa', 
        padding: '1rem 1.5rem', 
        borderBottom: '1px solid #e9ecef',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '1.1rem' }}>Sensor Data</h3>
        <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
          {tableData.length} data points
        </div>
      </div>

      <div style={{ overflowX: 'auto', maxHeight: '500px', overflowY: 'auto' }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          fontSize: '0.9rem'
        }}>
          <thead>
            <tr style={{ background: '#e9ecef' }}>
              <th style={{ 
                padding: '0.75rem', 
                textAlign: 'left', 
                borderBottom: '2px solid #dee2e6',
                position: 'sticky',
                top: 0,
                background: '#e9ecef',
                zIndex: 10,
                fontWeight: '600',
                color: '#2c3e50'
              }}>
                Time
              </th>
              <th style={{ 
                padding: '0.75rem', 
                textAlign: 'left', 
                borderBottom: '2px solid #dee2e6',
                position: 'sticky',
                top: 0,
                background: '#e9ecef',
                zIndex: 10,
                fontWeight: '600',
                color: '#2c3e50'
              }}>
                Buoy
              </th>
              {selectedFields.map(fieldKey => (
                <th key={fieldKey} style={{ 
                  padding: '0.75rem', 
                  textAlign: 'left', 
                  borderBottom: '2px solid #dee2e6',
                  position: 'sticky',
                  top: 0,
                  background: '#e9ecef',
                  zIndex: 10,
                  fontWeight: '600',
                  color: '#2c3e50'
                }}>
                  {getLabel(fieldKey)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid #f1f3f4' }}>
                <td style={{ 
                  padding: '0.75rem',
                  fontFamily: 'monospace',
                  fontWeight: '500',
                  color: '#2c3e50'
                }}>
                  {row.time.replace('T', ' ').slice(0, 16)}
                </td>
                <td style={{ 
                  padding: '0.75rem',
                  fontWeight: '600',
                  color: '#007bff'
                }}>
                  {row.buoy_id || 'Unknown'}
                </td>
                {selectedFields.map(fieldKey => {
                  const value = row[fieldKey];
                  const isCO2Mode = fieldKey === 'procv_co2:pCO2' || fieldKey === 'mini_co2:pCO2';
                  const mode = isCO2Mode ? row[`${fieldKey}_mode`] : undefined;

                  const isBad = isBadDataPoint(fieldKey, value);
                  const excluded = isExcluded(row, fieldKey);

                  let backgroundColor = 'white';
                  let borderLeft = 'none';
                  let cursor = 'default';
                  let textDecoration = 'none';

                  if (excluded) {
                    // Already excluded - show grayed out
                    backgroundColor = '#f8f9fa';
                    textDecoration = 'line-through';
                    cursor = 'default';
                  } else if (isBad) {
                    // Bad data - show in red and clickable
                    backgroundColor = '#f8d7da';
                    borderLeft = '3px solid #dc3545';
                    cursor = 'pointer';
                  } else if (isCO2Mode) {
                    // Normal pCO2 coloring by mode (ProCV + Mini)
                    const m = (mode || '').toString().toLowerCase().replace(/\s+/g, '_');
                    backgroundColor = m === 'a_m'
                      ? '#fff3cd'
                      : m === 'w_m'
                        ? '#d1ecf1'
                        : 'white';
                    borderLeft = m === 'a_m'
                      ? '3px solid #ffc107'
                      : m === 'w_m'
                        ? '3px solid #17a2b8'
                        : 'none';
                  }

                  return (
                    <td
                      key={fieldKey}
                      style={{
                        padding: '0.75rem',
                        backgroundColor,
                        borderLeft,
                        position: 'relative',
                        cursor,
                        textDecoration
                      }}
                      onClick={isBad && !excluded ? () => handleBadDataClick(row, fieldKey) : undefined}
                      title={isBad && !excluded ? 'Click to exclude this bad data point' : undefined}
                      data-bad-data={isBad && !excluded ? 'true' : undefined}
                    >
                      {typeof value === 'number' ? value.toFixed(2) : value || '-'}
                      {mode && !excluded && (() => {
                        const m = mode.toString().toLowerCase().replace(/\s+/g, '_');
                        const tag = m === 'a_m' ? '(Air)' : m === 'w_m' ? '(Water)' : '';
                        return tag ? (
                          <span style={{
                            marginLeft: '0.5rem',
                            fontSize: '0.8rem',
                            color: '#6c757d'
                          }}>
                            {tag}
                          </span>
                        ) : null;
                      })()}
                      {isBad && !excluded && (
                        <span style={{ 
                          marginLeft: '0.5rem', 
                          fontSize: '0.8rem',
                          color: '#dc3545',
                          fontWeight: 'bold'
                        }}>
                          ⚠ BAD
                        </span>
                      )}
                      {excluded && (
                        <span style={{ 
                          marginLeft: '0.5rem', 
                          fontSize: '0.8rem',
                          color: '#6c757d'
                        }}>
                          EXCLUDED
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ 
        background: '#f8f9fa', 
        padding: '1rem 1.5rem', 
        borderTop: '1px solid #e9ecef',
        fontSize: '0.9rem',
        color: '#6c757d'
      }}>
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffc107',
              borderRadius: '2px'
            }}></div>
            <span>Air Measurement</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#d1ecf1', 
              border: '1px solid #17a2b8',
              borderRadius: '2px'
            }}></div>
            <span>Water Measurement</span>
          </div>
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              cursor: 'pointer',
              padding: '0.25rem',
              borderRadius: '4px',
              transition: 'background-color 0.2s'
            }}
            onClick={scrollToFirstBadData}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            title="Click to scroll to first bad data point"
          >
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#f8d7da', 
              border: '1px solid #dc3545',
              borderRadius: '2px'
            }}></div>
            <span>Bad Data (click to find)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#f8f9fa', 
              border: '1px solid #6c757d',
              borderRadius: '2px'
            }}></div>
            <span>Excluded Data</span>
          </div>
        </div>
      </div>
    </div>
  );
} 